


const TEXT_LUMINANCE_THRESHOLD = 0.35;

export function hexToRgb(color) {
    if (!color) return { r: 0, g: 0, b: 0 };

    
    if (color.includes('rgb')) {
        const match = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
        if (match) {
            return {
                r: Math.min(255, Math.max(0, parseInt(match[1], 10))),
                g: Math.min(255, Math.max(0, parseInt(match[2], 10))),
                b: Math.min(255, Math.max(0, parseInt(match[3], 10)))
            };
        }
    }

    
    let cleanHex = color.replace('#', '').trim();
    
    
    if (cleanHex.length === 3) {
        cleanHex = cleanHex.split('').map(c => c + c).join('');
    }

    
    if (cleanHex.length !== 6) {
        console.warn('[Luminance] Invalid color format:', color);
        return { r: 0, g: 0, b: 0 };
    }

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return {
        r: isNaN(r) ? 0 : r,
        g: isNaN(g) ? 0 : g,
        b: isNaN(b) ? 0 : b
    };
}


export function calculateRelativeLuminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}


export function getLuminanceFromHex(hex) {
    const { r, g, b } = hexToRgb(hex);
    return calculateRelativeLuminance(r, g, b);
}


export function getOptimalTextColor(backgroundLuminance, options = {}) {
    const {
        lightColor = '#fff',
        darkColor = '#000',
        threshold = TEXT_LUMINANCE_THRESHOLD
    } = options;

    
    const luminance = typeof backgroundLuminance === 'string' 
        ? getLuminanceFromHex(backgroundLuminance)
        : backgroundLuminance;

    return luminance > threshold ? darkColor : lightColor;
}


export function getOptimalTextColorFromCssVariable(cssVariableName) {
    const varName = cssVariableName.startsWith('--') ? cssVariableName : `--${cssVariableName}`;
    const computedStyle = getComputedStyle(document.documentElement);
    const colorValue = computedStyle.getPropertyValue(varName).trim();
    
    if (!colorValue) {
        return '#fff';
    }

    return getOptimalTextColor(colorValue);
}


export function calculateEffectiveLuminanceWithOverlay(baseLuminance, overlayOpacity = 0.25) {
    
    const whiteLuminance = 1.0;
    
    return baseLuminance * (1 - overlayOpacity) + whiteLuminance * overlayOpacity;
}


export function computeOptimalTextColor(backgroundVar, computedStyle, options = {}) {
    const {
        accountForShading = false,
        shadingOpacity = 0.25
    } = options;

    const varName = backgroundVar.startsWith('--') ? backgroundVar : `--${backgroundVar}`;
    const bgColor = computedStyle.getPropertyValue(varName).trim();
    if (!bgColor) return null;

    const { r, g, b } = hexToRgb(bgColor);
    let luminance = calculateRelativeLuminance(r, g, b);

    if (accountForShading) {
        luminance = calculateEffectiveLuminanceWithOverlay(luminance, shadingOpacity);
    }

    return getOptimalTextColor(luminance);
}


export function applyOptimalTextColor(backgroundVar, textVar, options = {}) {
    const computedStyle = getComputedStyle(document.documentElement);
    const optimalColor = computeOptimalTextColor(backgroundVar, computedStyle, options);
    if (!optimalColor) return;

    const textVarName = textVar.startsWith('--') ? textVar : `--${textVar}`;
    document.documentElement.style.setProperty(textVarName, optimalColor);
}


export function applyShadedTextContrast(backgroundVar, textVar, shadingOpacity = 0.25) {
    applyOptimalTextColor(backgroundVar, textVar, {
        accountForShading: true,
        shadingOpacity
    });
}


export function generateTextColors(colors = {}) {
    const textColors = {};

    const frame = colors.main;
    if (frame) {
        const frameLum = getLuminanceFromHex(frame);
        const normal = getOptimalTextColor(frameLum);
        const shaded = getOptimalTextColor(calculateEffectiveLuminanceWithOverlay(frameLum, 0.25));
        textColors['--color-text-main'] = normal;
        textColors['--color-text-secondary'] = normal;
        textColors['--color-text-primary'] = normal;
        textColors['--color-text-muted'] = normal;
        textColors['--color-text-title'] = shaded;
    }

    if (colors.secondary) {
        textColors['--color-text-on-secondary'] = getOptimalTextColor(colors.secondary);
        textColors['--color-dropdown-text'] = getOptimalTextColor(colors.secondary);
    }

    if (colors.tertiary) {
        textColors['--color-text-on-tertiary'] = getOptimalTextColor(colors.tertiary);
    }

    if (colors.highlight1) {
        textColors['--color-text-on-highlight1'] = getOptimalTextColor(colors.highlight1);
    }

    if (colors.highlight2) {
        const h2Color = getOptimalTextColor(colors.highlight2);
        textColors['--color-text-on-active'] = h2Color;
        textColors['--color-chat-bubble-user-text'] = h2Color;
        textColors['--color-queue-banner-text'] = h2Color;

        const tooltipBg = h2Color === '#fff' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
        const tooltipLabel = h2Color === '#fff' ? '#000' : '#fff';
        const trackText = h2Color === '#fff' ? '#fff' : '#000';
        textColors['--color-volume-tooltip-bg'] = tooltipBg;
        textColors['--color-volume-tooltip-label'] = tooltipLabel;
        textColors['--color-volume-track-text'] = trackText;
    }

    if (colors.chatBubbleAiBg) {
        textColors['--color-chat-bubble-ai-text'] = getOptimalTextColor(colors.chatBubbleAiBg);
    }

    textColors['--color-waveform-comment-text'] = '#000';
    textColors['--color-waveform-bg'] = 'var(--color-windows-bg)';

    return textColors;
}


export function setTextColorVariables(textColors = {}) {
    for (const [name, value] of Object.entries(textColors)) {
        if (value) {
            document.documentElement.style.setProperty(name, value);
        }
    }
}

let _textContrastDebounceTimer = null;


export function updateAllTextContrast() {
    if (_textContrastDebounceTimer) {
        clearTimeout(_textContrastDebounceTimer);
    }
    _textContrastDebounceTimer = setTimeout(() => {
        _textContrastDebounceTimer = null;
        _updateAllTextContrastImpl();
    }, 50);
}

function _updateAllTextContrastImpl() {
    const style = getComputedStyle(document.documentElement);
    const updates = [];

    const frameColor = '--color-frame';
    const queueBannerBg = style.getPropertyValue('--color-accent-highlight-2').trim();
    const windowsBg = style.getPropertyValue('--color-windows-bg').trim();

    const simple = (bgVar, textVar, opts = {}) => {
        const color = computeOptimalTextColor(bgVar, style, opts);
        if (color) updates.push([textVar, color]);
    };

    simple(frameColor, '--color-text-main');
    simple(frameColor, '--color-text-secondary');
    simple(frameColor, '--color-text-primary');
    simple(frameColor, '--color-text-muted');
    simple(frameColor, '--color-text-title', { accountForShading: true, shadingOpacity: 0.25 });
    simple('--color-windows-bg', '--color-text-on-secondary');
    simple('--color-console-main', '--color-text-on-tertiary');
    simple('--color-active-highlight', '--color-text-on-active');
    simple('--color-accent-highlight-1', '--color-text-on-highlight1');
    simple('--color-active-highlight', '--color-chat-bubble-user-text');
    simple('--color-chat-bubble-ai-bg', '--color-chat-bubble-ai-text');

    updates.push(['--color-waveform-bg', 'var(--color-windows-bg)']);

    if (queueBannerBg) {
        const { r, g, b } = hexToRgb(queueBannerBg);
        const queueText = getOptimalTextColor(calculateRelativeLuminance(r, g, b));
        updates.push(['--color-queue-banner-text', queueText]);
    } else {
        updates.push(['--color-queue-banner-text', '#000']);
    }

    if (windowsBg) {
        const { r, g, b } = hexToRgb(windowsBg);
        const dropdownText = getOptimalTextColor(calculateRelativeLuminance(r, g, b));
        updates.push(['--color-dropdown-text', dropdownText]);
    } else {
        updates.push(['--color-dropdown-text', '#fff']);
    }

    updates.push(['--color-waveform-comment-text', '#000']);

    if (queueBannerBg) {
        const { r, g, b } = hexToRgb(queueBannerBg);
        const highlightText = getOptimalTextColor(calculateRelativeLuminance(r, g, b));
        const tooltipBg = highlightText === '#fff' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
        const tooltipLabel = highlightText === '#fff' ? '#000' : '#fff';
        const trackText = highlightText === '#fff' ? '#fff' : '#000';
        updates.push(['--color-volume-tooltip-bg', tooltipBg]);
        updates.push(['--color-volume-tooltip-label', tooltipLabel]);
        updates.push(['--color-volume-track-text', trackText]);

        const tooltip = document.getElementById('volume-tooltip');
        if (tooltip) {
            requestAnimationFrame(() => {
                tooltip.style.backgroundColor = tooltipBg;
                tooltip.querySelectorAll('.tooltip-label').forEach(label => {
                    label.style.color = tooltipLabel;
                });
            });
        }
    } else {
        updates.push(['--color-volume-tooltip-bg', 'rgba(0, 0, 0, 0.9)']);
        updates.push(['--color-volume-tooltip-label', '#9ca3af']);
        updates.push(['--color-volume-track-text', 'rgba(255, 255, 255, 0.9)']);
    }

    for (const [name, value] of updates) {
        document.documentElement.style.setProperty(name, value);
    }
}


export function updateWaveformBackground() {
    // Tie the waveform background directly to the user's 'secondary' color group
    document.documentElement.style.setProperty('--color-waveform-bg', 'var(--color-windows-bg)');
}


export function updateQueueBannerTextContrast() {
    const computedStyle = getComputedStyle(document.documentElement);
    const bannerBg = computedStyle.getPropertyValue('--color-accent-highlight-2').trim();
    
    if (!bannerBg) {
        
        document.documentElement.style.setProperty('--color-queue-banner-text', '#000');
        return;
    }

    const { r, g, b } = hexToRgb(bannerBg);
    const luminance = calculateRelativeLuminance(r, g, b);
    
    
    
    const textColor = getOptimalTextColor(luminance);
    document.documentElement.style.setProperty('--color-queue-banner-text', textColor);
}


export function updateVolumeTooltipContrast() {
    const computedStyle = getComputedStyle(document.documentElement);
    const highlight2Color = computedStyle.getPropertyValue('--color-accent-highlight-2').trim();
    
    if (!highlight2Color) {
        
        document.documentElement.style.setProperty('--color-volume-tooltip-bg', 'rgba(0, 0, 0, 0.9)');
        document.documentElement.style.setProperty('--color-volume-tooltip-label', '#9ca3af');
        document.documentElement.style.setProperty('--color-volume-track-text', 'rgba(255, 255, 255, 0.9)');
        return;
    }

    const { r, g, b } = hexToRgb(highlight2Color);
    const luminance = calculateRelativeLuminance(r, g, b);
    
    
    
    const highlightTextColor = getOptimalTextColor(luminance);
    const bgColor = highlightTextColor === '#fff' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
    const labelColor = highlightTextColor === '#fff' ? '#000' : '#fff';
    const trackTextColor = highlightTextColor === '#fff' ? '#fff' : '#000';
    
    document.documentElement.style.setProperty('--color-volume-tooltip-bg', bgColor);
    document.documentElement.style.setProperty('--color-volume-tooltip-label', labelColor);
    document.documentElement.style.setProperty('--color-volume-track-text', trackTextColor);
    
    
    const tooltip = document.getElementById('volume-tooltip');
    if (tooltip) {
        tooltip.style.backgroundColor = bgColor;
    }
    
    
    const labels = tooltip?.querySelectorAll('.tooltip-label');
    if (labels) {
        labels.forEach(label => {
            label.style.color = labelColor;
        });
    }
}


export function updateWaveformCommentTextContrast() {
    
    
    document.documentElement.style.setProperty('--color-waveform-comment-text', '#000');
}


export function getTextRecommendation(backgroundLuminance) {
    const luminance = typeof backgroundLuminance === 'string' 
        ? getLuminanceFromHex(backgroundLuminance)
        : backgroundLuminance;
    
    return luminance > TEXT_LUMINANCE_THRESHOLD ? 'dark' : 'light';
}


export function updateDropdownTextContrast() {
    const computedStyle = getComputedStyle(document.documentElement);
    const windowsBg = computedStyle.getPropertyValue('--color-windows-bg').trim();
    
    if (!windowsBg) {
        document.documentElement.style.setProperty('--color-dropdown-text', '#fff');
        return;
    }

    const { r, g, b } = hexToRgb(windowsBg);
    const luminance = calculateRelativeLuminance(r, g, b);
    
    const textColor = getOptimalTextColor(luminance);
    document.documentElement.style.setProperty('--color-dropdown-text', textColor);
}


function parseCssColor(color) {
    if (!color || color === 'transparent') return null;

    const rgbMatch = color.match(/rgba?\s*\(\s*(\d+\.?\d*)\s*(?:,|\s)\s*(\d+\.?\d*)\s*(?:,|\s)\s*(\d+\.?\d*)\s*(?:[,/]\s*(\d*\.?\d+%?)\s*)?\)/);
    if (rgbMatch) {
        const alphaValue = rgbMatch[4];
        const alpha = alphaValue === undefined
            ? 1
            : alphaValue.endsWith('%')
                ? parseFloat(alphaValue) / 100
                : parseFloat(alphaValue);
        return {
            r: Math.min(255, Math.max(0, parseFloat(rgbMatch[1]))),
            g: Math.min(255, Math.max(0, parseFloat(rgbMatch[2]))),
            b: Math.min(255, Math.max(0, parseFloat(rgbMatch[3]))),
            a: Math.min(1, Math.max(0, alpha))
        };
    }

    const hex = color.replace('#', '').trim();
    if ([3, 4, 6, 8].includes(hex.length)) {
        const expanded = hex.length <= 4
            ? hex.split('').map(character => character + character).join('')
            : hex;
        const r = parseInt(expanded.substring(0, 2), 16);
        const g = parseInt(expanded.substring(2, 4), 16);
        const b = parseInt(expanded.substring(4, 6), 16);
        const a = expanded.length === 8 ? parseInt(expanded.substring(6, 8), 16) / 255 : 1;
        if (![r, g, b, a].some(Number.isNaN)) {
            return { r, g, b, a };
        }
    }

    return null;
}


function alphaCompositeOver(src, dst) {
    const a = src.a + dst.a * (1 - src.a);
    if (a <= 0) return { r: 0, g: 0, b: 0, a: 0 };
    return {
        r: (src.r * src.a + dst.r * dst.a * (1 - src.a)) / a,
        g: (src.g * src.a + dst.g * dst.a * (1 - src.a)) / a,
        b: (src.b * src.a + dst.b * dst.a * (1 - src.a)) / a,
        a
    };
}


export function getEffectiveBackgroundColor(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return { r: 255, g: 255, b: 255, a: 1 };

    const layers = [];
    let current = el;
    const stopAt = document.documentElement;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
        const bg = parseCssColor(window.getComputedStyle(current).backgroundColor);
        if (bg && bg.a > 0) layers.push(bg);
        if (current === stopAt) break;
        current = current.parentElement;
    }

    let color = { r: 255, g: 255, b: 255, a: 1 };
    for (let index = layers.length - 1; index >= 0; index -= 1) {
        color = alphaCompositeOver(layers[index], color);
    }

    return color;
}


const EXCLUDED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'CANVAS', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK', 'META', 'LINK']);


function shouldSkipElement(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return true;
    if (EXCLUDED_TAGS.has(el.tagName)) return true;
    if (el.dataset && el.dataset.luminance === 'preserve') return true;
    return false;
}


function hasDirectTextContent(el) {
    for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
            return true;
        }
    }
    return false;
}


function isIconElement(el) {
    if (el.tagName === 'I' || el.tagName === 'svg' || el.tagName === 'path' || el.tagName === 'polygon' || el.tagName === 'rect' || el.tagName === 'circle') return true;
    if (el.classList && (el.classList.contains('fas') || el.classList.contains('fab') || el.classList.contains('far') || el.classList.contains('fal'))) return true;
    return false;
}


const FORM_CONTROL_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON']);
const SVG_PAINT_TAGS = new Set(['svg', 'path', 'polygon', 'rect', 'circle']);
const ADAPTIVE_SHADOW_ROOT_SELECTOR = '#channel-title-banner, #header-ticket-btn, #header-coins-display, .jukebot-title-btn[data-mode="chat"], .jukebot-title-btn[data-mode="mail"], .slot-0-play-icon';

function setStyleProperty(el, property, value, priority = 'important') {
    const currentValue = el.style.getPropertyValue(property).trim().toLowerCase();
    const equivalentValues = value === '#fff'
        ? new Set(['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'])
        : value === '#000'
            ? new Set(['#000', '#000000', 'black', 'rgb(0, 0, 0)'])
            : new Set([value]);
    if (equivalentValues.has(currentValue) && el.style.getPropertyPriority(property) === priority) return;
    el.style.setProperty(property, value, priority);
}

function applyAdaptiveTextShadow(el, foregroundColor) {
    if (!el.closest(ADAPTIVE_SHADOW_ROOT_SELECTOR)) return;

    const hasText = hasDirectTextContent(el);
    const isIcon = isIconElement(el);
    const isFormControl = FORM_CONTROL_TAGS.has(el.tagName);
    if (!hasText && !isIcon && !isFormControl) return;

    const foreground = parseCssColor(foregroundColor || window.getComputedStyle(el).color);
    if (!foreground) return;

    const foregroundLuminance = calculateRelativeLuminance(foreground.r, foreground.g, foreground.b);
    const shadowColor = foregroundLuminance > TEXT_LUMINANCE_THRESHOLD ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)';
    setStyleProperty(el, 'text-shadow', `0 0 2px ${shadowColor}, 0 0 4px ${shadowColor}`);
}


export function applyElementContrast(el) {
    if (shouldSkipElement(el)) {
        applyAdaptiveTextShadow(el);
        return;
    }

    const hasText = hasDirectTextContent(el);
    const hasGeneratedPlaceholder = el.hasAttribute('data-placeholder');
    const isIcon = isIconElement(el);
    const isFormControl = FORM_CONTROL_TAGS.has(el.tagName);
    if (!hasText && !hasGeneratedPlaceholder && !isIcon && !isFormControl) return;

    const bg = getEffectiveBackgroundColor(el);
    const luminance = calculateRelativeLuminance(bg.r, bg.g, bg.b);
    const color = getOptimalTextColor(luminance);

    setStyleProperty(el, 'color', color);
    applyAdaptiveTextShadow(el, color);

    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || hasGeneratedPlaceholder) {
        setStyleProperty(el, '--luminance-placeholder-color', color, '');
        if (el.getAttribute('data-luminance-contrast') !== 'auto') {
            el.setAttribute('data-luminance-contrast', 'auto');
        }
    }

    if (isIcon && SVG_PAINT_TAGS.has(el.tagName)) {
        const computedStyle = window.getComputedStyle(el);
        if (computedStyle.fill !== 'none') setStyleProperty(el, 'fill', color);
        if (computedStyle.stroke !== 'none') setStyleProperty(el, 'stroke', color);
    }
}


export function applyContrastToSubtree(root) {
    if (!root || root.nodeType !== Node.ELEMENT_NODE || EXCLUDED_TAGS.has(root.tagName)) return;

    applyElementContrast(root);
    if (root.dataset && root.dataset.luminance === 'preserve') return;

    for (let i = 0; i < root.children.length; i++) {
        applyContrastToSubtree(root.children[i]);
    }
}
